import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, BarChart3 } from 'lucide-react';
import { StandardPageHeader } from '../../../components/layout/StandardPageHeader';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useGestaoTasks } from '../hooks/useGestaoTasks';
import { useGestaoKPIs } from '../hooks/useGestaoKPIs';
import { useGestaoMutations } from '../hooks/useGestaoMutations';
import { useMyTaskDetail } from '../../minha-mesa/hooks/useMyTaskDetail';
import { useGestaoStore } from '../store/gestaoStore';
import { GestaoKanbanBoard } from '../components/GestaoKanbanBoard';
import { GestaoListView } from '../components/GestaoListView';
import { GestaoTaskDrawer } from '../components/GestaoTaskDrawer';
import { useExecutionModuleContext } from '../../execution/context/ExecutionModuleContext';
import {
  desktopRibbonDividerClass,
  desktopRibbonSelectClass,
} from '../../../components/ribbon/DesktopRibbonControls';
import type { TaskInstanceStatus } from '../../../types/workflow-v9';
import type { WorkflowPriority } from '../../../types/workflow-v9';
import { getTaskLogs } from '../../shared/services/taskService';
import { useQuery } from '@tanstack/react-query';
import { taskKeys } from '../../shared/keys';
import OperationalMetricsBar from '../../../components/operational/OperationalMetricsBar';
import { useOperationalContext } from '../../../contexts/OperationalContext';

interface GestaoDashboardPageProps {
  compactMode?: boolean;
}

export const GestaoDashboardPage: React.FC<GestaoDashboardPageProps> = ({ compactMode = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const userId = user?.id ?? '';
  const userOrganizationId = user?.organization_id ?? user?.organizationId;
  const organizationId = workspace?.organizationId ?? userOrganizationId;
  const executionFilterParam = searchParams.get('executionId');
  const flowIdParam = searchParams.get('flowId');
  const flowNameParam = searchParams.get('flowName');

  const {
    activeFlowName,
    setActiveFlow,
    setActiveExecution,
    setActiveStage,
    setOperationalMetrics,
  } = useOperationalContext();

  const {
    viewMode,
    selectedTaskId,
    drawerOpen,
    filters,
    sortField,
    sortDirection,
    page,
    pageSize,
    setViewMode,
    openDrawer,
    closeDrawer,
    setFilters,
    resetFilters,
  } = useGestaoStore();

  const { items, total, isLoading, refetch } = useGestaoTasks({
    organizationId,
    filters,
    sortField,
    sortDirection,
    page,
    pageSize,
  });

  const { metrics, isLoading: kpisLoading } = useGestaoKPIs({ organizationId });
  const { setFiltersSlot } = useExecutionModuleContext();

  //    Ribbon filter slot (same pattern as Instâncias)                         
  const filtersSlot = useMemo(() => (
    <>
      <div className={desktopRibbonDividerClass} />
      <div className="flex items-center gap-[5px] px-1">
        {/* Search */}
        <div className="flex h-[30px] w-40 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={12} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            placeholder="Buscar..."
            className="w-full bg-transparent text-[12px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-secondary)]"
          />
        </div>
        {/* Status select */}
        <select
          value={filters.status[0] ?? 'all'}
          onChange={(e) =>
            setFilters({ status: e.target.value === 'all' ? [] : [e.target.value as TaskInstanceStatus] })
          }
          className={desktopRibbonSelectClass}
        >
          <option value="all">Status: Todos</option>
          <option value="pending">Pendente</option>
          <option value="in_progress">Em Andamento</option>
          <option value="completed">Concluída</option>
          <option value="failed">Falhou</option>
          <option value="skipped">Pulada</option>
          <option value="cancelled">Cancelada</option>
        </select>
        {/* Priority select */}
        <select
          value={filters.priority[0] ?? 'all'}
          onChange={(e) =>
            setFilters({ priority: e.target.value === 'all' ? [] : [e.target.value as WorkflowPriority] })
          }
          className={desktopRibbonSelectClass}
        >
          <option value="all">Criticidade: Todas</option>
          <option value="critica">Crítica</option>
          <option value="alta">Alta</option>
          <option value="media">Mdia</option>
          <option value="baixa">Baixa</option>
        </select>
        {/* SLA Vencido */}
        <label className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] font-mono text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={filters.overdue}
            onChange={(e) => setFilters({ overdue: e.target.checked })}
            className="h-3 w-3 rounded"
          />
          SLA Vencido
        </label>
        {/* Reset */}
        <button
          type="button"
          onClick={resetFilters}
          className="flex h-[30px] items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] font-mono text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          Limpar
        </button>
      </div>
    </>
  ), [filters, viewMode, setFilters, resetFilters, setViewMode]);

  useEffect(() => {
    setFiltersSlot(filtersSlot);
  }, [filtersSlot, setFiltersSlot]);

  useEffect(() => () => setFiltersSlot(null), [setFiltersSlot]);

  const { task: selectedTask, isLoading: detailLoading } = useMyTaskDetail(
    drawerOpen ? selectedTaskId : null,
  );

  const logsQuery = useQuery({
    queryKey: taskKeys.logs(selectedTaskId ?? ''),
    enabled: Boolean(drawerOpen && selectedTaskId),
    queryFn: () => getTaskLogs(selectedTaskId!),
    staleTime: 15_000,
  });

  const mutations = useGestaoMutations({
    onSuccess: async () => {
      await refetch();
      closeDrawer();
    },
  });

  useEffect(() => {
    if (flowIdParam || flowNameParam) {
      setActiveFlow(flowIdParam, flowNameParam);
    }
  }, [flowIdParam, flowNameParam, setActiveFlow]);

  useEffect(() => {
    if (!executionFilterParam || filters.instanceId === executionFilterParam) return;
    setFilters({ instanceId: executionFilterParam });
  }, [executionFilterParam, filters.instanceId, setFilters]);

  useEffect(() => {
    setOperationalMetrics({
      globalSla: metrics?.sla_rate_7d ?? 0,
      throughput7d: metrics?.throughput_7d ?? 0,
      userWip: metrics?.avg_wip_per_user ?? 0,
      bottleneckStage: metrics?.most_critical_stage ?? null,
    });
  }, [
    metrics?.avg_wip_per_user,
    metrics?.most_critical_stage,
    metrics?.sla_rate_7d,
    metrics?.throughput_7d,
    setOperationalMetrics,
  ]);

  const activeExecutions = useMemo(() => new Set(items.map((item) => item.instanceId)).size, [items]);

  const handleTaskClick = (taskId: string) => {
    const task = items.find((item) => item.id === taskId);
    if (task) {
      setActiveExecution(task.instanceId, task.referenceCode ?? task.instanceTitle ?? task.instanceId, task.metadata?.template_id as string | null, activeFlowName);
      setActiveStage(task.stageId, task.stageName);
    }
    openDrawer(taskId);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-[var(--bg-workspace)]">
      {compactMode ? (
        <div className="border-b border-slate-200/80 px-6 py-1.5 dark:border-slate-800">
          <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-800 dark:text-slate-100">Kanban Operacional</div>
        </div>
      ) : (
        <>
          <StandardPageHeader title="Análise" icon={<BarChart3 size={20} />} />
          <div className="px-6">
            <OperationalMetricsBar />
          </div>
          <div className="mx-5 mt-2 rounded border border-[var(--border-default)] bg-zinc-100 dark:bg-white/5 px-4 py-1.5 text-xs text-zinc-600 dark:text-zinc-300">
            <span className="font-semibold">Fluxo:</span> {activeFlowName ?? flowNameParam ?? 'Vendas B2B'}
            <span className="mx-3">|</span>
            <span className="font-semibold">Execuções Ativas:</span> {activeExecutions}
            <span className="mx-3">|</span>
            <span className="font-semibold">SLA Global:</span> {(metrics?.sla_rate_7d ?? 0).toFixed(1)}%
          </div>
        </>
      )}

      {!compactMode ? (
        <div className="border-b border-[var(--border-default)] px-5 py-1.5">
          <div className="font-mono text-[10px] font-normal uppercase tracking-widest text-zinc-500">
            {`PEND: ${kpisLoading ? '...' : total} | ATRAS: ${kpisLoading ? '...' : (metrics?.tasks_overdue ?? 0)} | SLA_7D: ${kpisLoading ? '...' : `${metrics?.sla_rate_7d ?? 0}%`} | TH7D: ${kpisLoading ? '...' : (metrics?.throughput_7d ?? 0)} | WIP_USR: ${kpisLoading ? '...' : (metrics?.avg_wip_per_user ?? 0)}`}
          </div>
        </div>
      ) : null}



      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden">
          {viewMode === 'kanban' ? (
            <GestaoKanbanBoard
              tasks={items}
              onTaskClick={handleTaskClick}
              isLoading={isLoading}
              onOpenExecution={(executionId) => {
                navigate(`/execucoes/instancias/${encodeURIComponent(executionId)}`);
              }}
              compactMode={compactMode}
            />
          ) : (
            <GestaoListView
              tasks={items}
              onTaskClick={handleTaskClick}
              isLoading={isLoading}
            />
          )}
        </div>

        {drawerOpen ? (
          <GestaoTaskDrawer
            task={selectedTask}
            isLoading={detailLoading}
            logs={logsQuery.data ?? []}
            logsLoading={logsQuery.isPending}
            onClose={closeDrawer}
            onReassign={mutations.reassignTask}
            onSkip={mutations.skipTask}
            onAdjustSLA={mutations.adjustSLA}
            isReassigning={mutations.isReassigning}
            isSkipping={mutations.isSkipping}
            isAdjustingSLA={mutations.isAdjustingSLA}
            currentUserId={userId}
            onOpenExecution={(executionId) => {
              navigate(`/execucoes/instancias/${encodeURIComponent(executionId)}`);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};
